// src/pages/ManageMenusPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from '../components/loadingspinner';
import Switch from '../components/Switch';
import { trackAdminAction } from '../services/analyticsService';

function ManageMenusPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Get restaurant info
        const restaurantDocRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantDocRef);

        if (!restaurantSnap.exists()) {
          setError('Restaurant not found. Please check the URL and try again.');
          setLoading(false);
          return;
        }

        const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
        setRestaurant(restaurantData);

        // Get all menus for this restaurant
        const menusRef = collection(db, 'restaurants', restaurantId, 'menus');
        const menusQuery = query(menusRef, orderBy('createdAt', 'desc'));
        const menusSnap = await getDocs(menusQuery);
        
        const menusData = menusSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Check for legacy menu items in the default menuItems collection
        const legacyMenuItemsRef = collection(db, 'restaurants', restaurantId, 'menuItems');
        const legacyMenuItemsSnap = await getDocs(legacyMenuItemsRef);
        
        let finalMenusData = [...menusData];
        
        // If legacy menu items exist, add them as a "Main Menu"
        if (!legacyMenuItemsSnap.empty) {
          const legacyMenu = {
            id: 'legacy', // Special ID to identify legacy menu
            name: 'Main Menu',
            description: 'Your original menu items',
            isActive: menusData.length === 0, // Active if it's the only menu
            createdAt: new Date(0), // Set to earliest date so it appears last when sorted by desc
            isLegacy: true // Flag to identify this as a legacy menu
          };
          
          finalMenusData.push(legacyMenu);
        }

        setMenus(finalMenusData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchData();
    }
  }, [restaurantId]);

  const handleAddMenu = async (e) => {
    e.preventDefault();
    
    if (!newMenuName.trim()) {
      setError('Menu name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // Create new menu document
      const menusRef = collection(db, 'restaurants', restaurantId, 'menus');
      const newMenuData = {
        name: newMenuName.trim(),
        isActive: menus.length === 0, // First menu is active by default
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(menusRef, newMenuData);
      
      // Add to local state
      const newMenu = {
        id: docRef.id,
        ...newMenuData
      };
      
      setMenus([newMenu, ...menus]);
      setNewMenuName('');
      setShowAddForm(false);
      setSuccessMessage('Menu created successfully!');
      
      trackAdminAction('create_menu', { restaurantId, menuId: docRef.id, menuName: newMenuName });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error creating menu:', error);
      setError('Failed to create menu. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (menuId, currentActive) => {
    try {
      setError('');
      
      // Simply toggle the active state of the selected menu
      const menuRef = doc(db, 'restaurants', restaurantId, 'menus', menuId);
      await updateDoc(menuRef, { 
        isActive: !currentActive,
        updatedAt: new Date()
      });
      
      // Update local state
      setMenus(menus.map(menu => 
        menu.id === menuId 
          ? { ...menu, isActive: !currentActive }
          : menu
      ));
      
      if (!currentActive) {
        trackAdminAction('activate_menu', { restaurantId, menuId });
        setSuccessMessage('Menu activated successfully!');
      } else {
        trackAdminAction('deactivate_menu', { restaurantId, menuId });
        setSuccessMessage('Menu deactivated successfully!');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error toggling menu active status:', error);
      setError('Failed to update menu status. Please try again.');
    }
  };

  const handleDeleteMenu = async (menuId, menuName) => {
    if (!window.confirm(`Are you sure you want to delete the menu "${menuName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      
      // Delete the menu document
      const menuRef = doc(db, 'restaurants', restaurantId, 'menus', menuId);
      await deleteDoc(menuRef);
      
      // Remove from local state
      setMenus(menus.filter(menu => menu.id !== menuId));
      
      trackAdminAction('delete_menu', { restaurantId, menuId, menuName });
      setSuccessMessage('Menu deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error deleting menu:', error);
      setError('Failed to delete menu. Please try again.');
    }
  };

  const handleEditMenu = (menuId) => {
    // Navigate to EditMenuPage with menu context
    // For legacy menu, don't include menuId parameter
    if (menuId === 'legacy') {
      navigate(`/edit/${restaurantId}`);
    } else {
      navigate(`/edit/${restaurantId}?menuId=${menuId}`);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !restaurant) {
    return (
      <div className="container centered">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate(`/dashboard/${restaurantId}`)} className="btn mt-4">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header Section */}
      <div className="header" style={{
        position: 'relative',
        width: '100%'
      }}>
        <div className="header-content" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          paddingTop: '10px'
        }}>
          {/* Restaurant Logo - Full Width */}
          <div style={{
            width: '100%',
            marginBottom: '0px',
            position: 'relative'
          }}>
            <button 
              className="btn button-primary" 
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                zIndex: 10,
                textDecoration: 'none',
                padding: '8px 8px',
                fontSize: '0.8rem',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                maxWidth: '60px',
                overflow: 'hidden'
              }}
              onClick={() => navigate(`/dashboard/${restaurantId}`)}
            >
              Back
            </button>
            {restaurant?.logoUrl && (
              <img 
                src={restaurant.logoUrl} 
                alt={`${restaurant.name} Logo`} 
                style={{ 
                  width: '100%',
                  height: 'auto',
                  maxHeight: '150px',
                  objectFit: 'cover'
                }} 
              />
            )}
          </div>
          
          <div style={{
            textAlign: 'center',
            width: '100%',
            marginTop: '10px'
          }}>
            <h1 style={{ 
              textAlign: 'center',
              margin: '0',
              fontSize: '2rem',
              fontWeight: 'bold',
              width: '100%'
            }}>{restaurant?.name}</h1>
          </div>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="error-message mb-4">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message mb-4">
          {successMessage}
        </div>
      )}

      {/* Page Title */}
      <div className="menu-items">
        <h1 style={{ 
          textAlign: 'center',
          fontSize: '1.7rem',
          fontWeight: 'bold',
          paddingTop: '.5rem',
          width: '100%'
        }}>Manage Menus</h1>
      </div>

      {/* Add New Menu Button */}
      <div className="edit-controls" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.4rem',
        width: '100%',
        paddingBottom: '0.5rem',
      }}>
        <button 
          className="btn btn-sm"
          style={{
            textDecoration: 'none',
            display: 'inline-block',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}
          onClick={() => {
            setShowAddForm(!showAddForm);
            setNewMenuName('');
            setError('');
          }}
        >
          {showAddForm ? 'Cancel' : 'Add New Menu'}
        </button>
      </div>

      {/* Add Menu Form */}
      {showAddForm && (
        <div className="item-form mt-4">
          <h3>Add New Menu</h3>
          <form onSubmit={handleAddMenu}>
            <div className="form-group">
              <label htmlFor="menuName">Menu Name*</label>
              <input
                type="text"
                id="menuName"
                className="form-control"
                value={newMenuName}
                onChange={(e) => setNewMenuName(e.target.value)}
                placeholder="e.g. Breakfast Menu, Dinner Menu, Special Menu"
                required
              />
            </div>
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Menu'}
              </button>
              <button 
                type="button" 
                className="btn btn-outline ml-2"
                onClick={() => {
                  setShowAddForm(false);
                  setNewMenuName('');
                  setError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Menus List */}
      <div className="menus-list mt-4">
        {menus.length === 0 ? (
          <div className="no-menus" style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#666'
          }}>
            <p>No menus created yet.</p>
            <p>Click "Add New Menu" to create your first menu.</p>
          </div>
        ) : (
          <div className="menus-grid">
            {menus.map((menu) => (
              <div key={menu.id} className="menu-card" style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: menu.isActive ? '#f0f8ff' : '#fff',
                borderColor: menu.isActive ? '#007bff' : '#ddd'
              }}>
                <div className="menu-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    margin: '0',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}>
                    {menu.name}
                    {menu.isActive && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.8rem',
                        color: '#007bff',
                        fontWeight: 'normal'
                      }}>
                        (Active)
                      </span>
                    )}
                  </h3>
                  
                  <div className="menu-status">
                    {/* Don't show toggle for legacy menu - it's always active if it's the only one */}
                    {!menu.isLegacy && (
                      <Switch 
                        isOn={menu.isActive} 
                        handleToggle={() => handleToggleActive(menu.id, menu.isActive)}
                      />
                    )}
                  </div>
                </div>
                
                <div className="menu-info" style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  marginBottom: '1rem'
                }}>
                  <p>Created: {menu.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</p>
                  {menu.updatedAt && (
                    <p>Updated: {menu.updatedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</p>
                  )}
                </div>
                
                <div className="menu-actions" style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditMenu(menu.id)}
                  >
                    Edit Menu Items
                  </button>
                  
                  {/* Don't show delete button for legacy menu */}
                  {!menu.isLegacy && (
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleDeleteMenu(menu.id, menu.name)}
                      style={{
                        color: '#dc3545',
                        borderColor: '#dc3545'
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageMenusPage;